import React, { Suspense, lazy, useState, useRef, type ReactNode } from 'react'
import { Switch, Redirect, useLocation, useParams } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import TreeRoute from '@fs/zion-tree-route'
import Subnav from '@fs/zion-tree-subnav-legacy'
import { pidRegexStr } from '@fs/zion-pid'
import { Global, css } from '@emotion/core'
import { useHeaderControls } from '@fs/zion-root'
import { boolAttr } from '@fs/zion-frontend-friends'
import usePreloadPreferences from './services/usePreloadPreferences'
import CenteredLoadingSpinner from './components/CenteredLoadingSpinner'
import MinHeightDecorator from './components/MinHeightDecorator'
import PedigreeRoutes from './PedigreeRoutes'

const flexGrowCss = css`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
`
const rootCss = css`
  :root {
    font-size: 16px;
  }
`
const RecentsPage = lazy(() => import('./pages/RecentsPage'))
const PrivatePeoplePage = lazy(() => import('./pages/PrivatePeoplePage'))

const JustPidRedirect = (): JSX.Element => {
  const { pid } = useParams()
  const { search } = useLocation()
  const url = `/pedigree/${pid}${search}`

  return <Redirect to={url} />
}

const subnavWrapCss = css`
  transition: 0.3s ease; // This transition matches what is in zion-root
  &[data-hide] {
    transform: translateY(calc(-1 * var(--subnav-height)));
    margin-bottom: calc(-1 * var(--subnav-height));
  }
`

/** Wrapper to hide the Subnav when putting pedigree in fullscreen */

const SubnavWrapper = (props): JSX.Element => {
  const { isVisible } = useHeaderControls()
  const ref = useRef<HTMLDivElement>(null)
  return (
    <div
      css={subnavWrapCss}
      ref={ref}
      style={{
        // Subnav is 52px high but we have the ref.current?.offsetHeight in case it changes
        '--subnav-height': `${ref.current?.offsetHeight || 52}px`,
      }}
      data-hide={boolAttr(!isVisible)}
    >
      <Subnav {...props} />
    </div>
  )
}

type TopLevelAppWrapperProps = {
  children: ReactNode
}

// Wrapper component to allow the preference-fetching hook to run *before* the app loads, so that we catch everything on initial load, instead of piecemeal.
const TopLevelAppWrapper = ({ children }: TopLevelAppWrapperProps): JSX.Element => {
  // Pre-cache all preferences that are used in the app
  usePreloadPreferences()

  return <>{children}</>
}

const CenteredLoadingSpinnerWithMinHeight = (): JSX.Element => (
  <MinHeightDecorator>
    <CenteredLoadingSpinner />
  </MinHeightDecorator>
)

function App(): JSX.Element {
  const { pathname } = useLocation()
  const [readOnlyMode, setReadOnlyMode] = useState(false)

  const page = pathname?.substr?.(1)?.split('/')?.[0]

  const pidMatch = `:pid(${pidRegexStr})?`

  return (
    <TopLevelAppWrapper>
      <ErrorBoundary>
        <Global styles={rootCss} />
        <SubnavWrapper page={page} showHelpOthers readOnlyMode={readOnlyMode} />
        <main id="skipToMainContent" css={flexGrowCss}>
          <Switch>
            <Redirect exact from="/" to="/pedigree/" />
            <Redirect from="/pedigree/sibling*" to="/pedigree/portrait*" />

            <TreeRoute
              LoadingComponent={CenteredLoadingSpinnerWithMinHeight}
              setReadOnlyMode={setReadOnlyMode}
              exact
              path="/private-people"
            >
              <Suspense fallback={<CenteredLoadingSpinnerWithMinHeight />}>
                <PrivatePeoplePage />
              </Suspense>
            </TreeRoute>

            <TreeRoute
              LoadingComponent={CenteredLoadingSpinnerWithMinHeight}
              setReadOnlyMode={setReadOnlyMode}
              exact
              path="/recents"
            >
              <Suspense fallback={<CenteredLoadingSpinnerWithMinHeight />}>
                <RecentsPage />
              </Suspense>
            </TreeRoute>

            {/* /tree/MMMM-MMM */}
            <TreeRoute
              LoadingComponent={CenteredLoadingSpinner}
              setReadOnlyMode={setReadOnlyMode}
              exact
              path={`/${pidMatch}`}
              component={JustPidRedirect}
            />
            {/* R9 Pedigree Routes. This must be the last entry. Otherwise we will get side-effects from react router */}
            <TreeRoute path="/pedigree*">
              <PedigreeRoutes setReadOnlyMode={setReadOnlyMode} />
            </TreeRoute>
          </Switch>
        </main>
      </ErrorBoundary>
    </TopLevelAppWrapper>
  )
}
export default App
